<template>
  <div class="login-form-wrapper">
    <h6 class="login-title text-uppercase d-flex justify-content-center">
      {{ title }}
    </h6>

    <transition name="slide">
      <b-alert variant="danger" :show="feedback === null ? false : true">{{
        feedback
      }}</b-alert>
    </transition>
    <transition name="slide">
      <b-alert variant="danger" :show="booleanError">{{ errorMsg }}</b-alert>
    </transition>

    <b-form ref="login-form" :action="absiRedirect" name="login" method="post">
      <input type="hidden" name="site" value="www2" />

      <b-input-group class="login-input-wrapper">
        <b-form-input
          class="login-form-input"
          id="username"
          type="text"
          placeholder="Brugernavn"
          v-model="username"
          required
          name="loginnavn"
        ></b-form-input>

        <b-input-group-append class="login-input-append" is-text>
          <b-icon-person-fill class="login-input-icon"></b-icon-person-fill>
        </b-input-group-append>
      </b-input-group>

      <b-input-group class="login-input-wrapper">
        <b-form-input
          class="login-form-input"
          id="password"
          type="password"
          required
          placeholder="Kodeord"
          v-model="password"
          name="passwd"
        ></b-form-input>

        <b-input-group-append class="login-input-append" is-text>
          <b-icon-key-fill class="login-input-icon"></b-icon-key-fill>
        </b-input-group-append>
      </b-input-group>

      <vue-recaptcha
        ref="recaptcha"
        :sitekey="sitekey"
        @verify="onCaptchaVerified"
        @expired="onCaptchaExpired"
        @error="onCaptchaError"
        :loadRecaptchaScript="true"
        class="login-recaptcha"
        :size="captchaSize"
        :language="lang"
      >
      </vue-recaptcha>

      <b-button
        class="
          login-button
          text-uppercase
          w-100
        "
        @click.prevent="onSubmit"
        type="submit"
        value="Log ind"
        :disabled="submitting"
      >
        <b-spinner
          v-if="submitting"
          small
          class="my-auto mr-1"
          label="Logger ind"
        ></b-spinner>
        <span class="login-button-text">Log ind</span>
      </b-button>
    </b-form>

    <div class="forgotten-info-wrapper text-center">
      <p class="forgotten-info">Glemt log ind informationer?</p>
      <p class="forgotten-info" id="forgotten-contact">
        Kontakt absi på <a href="tel:+4533267839">+45 3326 7839</a>
      </p>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from "vue-recaptcha";

export default {
  name: "LoginForm",
  components: { VueRecaptcha },
  props: {
    captchaSize: {
      type: String,
      default: "normal",
    },
    title: {
      type: String,
      default: "",
    }
  },
  data() {
    return {
      username: null,
      password: null,
      submitting: false,
      feedback: null,
      sitekey: process.env.VUE_APP_GOOGLE_CAPTHCA_SITE_KEY,
      absiRedirect: process.env.VUE_APP_ABSI_LOGIN_URL,
      isVerified: false,
      lang: "da",
    };
  },
  computed: {
    isError() {
      return this.$route.params.errormsg;
    },
    booleanError() {
      if (this.feedback === null && this.isError) {
        return true;
      } else {
        return false;
      }
    },
    errorMsg() {
      if (this.isError === "captcha error") {
        return "Noget gik galt med at verificere, at du ikke er en robot.";
      } else {
        return "Forkert brugernavn eller kodeord.";
      }
    },
  },
  methods: {
    onSubmit() {
      try {
        if (!this.username && !this.password) {
          this.submitting = false;
          throw "Venligst indtast brugernavn og kodeord.";
        }

        if (!this.username) {
          this.submitting = false;
          throw "Venligst indtast brugernavn.";
        }

        if (!this.password) {
          this.submitting = false;
          throw "Venligst indtast kodeord.";
        }

        if (!this.isVerified) {
          this.submitting = false;
          throw "Venligst verificer at du ikke er en robot.";
        }

        this.submitting = true;
        this.feedback = null;
        this.$refs["login-form"].submit();
      } catch (error) {
        this.feedback = error;
      }
    },
    onCaptchaVerified(token) {
      if (token) {
        this.isVerified = true;
      }
    },
    onCaptchaExpired() {
      this.feedback = "Captcha er udløbet, prøv igen";
      this.$refs.recaptcha.reset();
    },
    onCaptchaError() {
      this.feedback = "Der skete en fejl ved captcha verificering";
    },
  },
};
</script>

<style lang="scss" scoped>
// Transition for entering and shifting of feedback and error
.slide-enter-active {
  -webkit-transition: all 0.3s ease-in;
  -moz-transition: all 0.3s ease-in;
  -o-transition: all 0.3s ease-in;
  -ms-transition: all 0.3s ease-in;
  transition: all 0.3s ease-in;
}
.slide-leave-active {
  -webkit-transition: all 0.3s ease-out;
  -moz-transition: all 0.3s ease-out;
  -o-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
}
.slide-enter-to,
.slide-leave {
  max-height: 800px;
  overflow: hidden;
}
.slide-enter,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}

// Login Form
.login-form-wrapper {
  .login-title {
    color: $senary;
  }

  .login-input-wrapper {
    .login-form-input {
      padding: 10px;
      margin: 10px 0;
      border: none;
      background-color: $septenary !important;
    }

    .login-input-append {
      .input-group-text {
        background-color: $septenary;
        border: none;
        margin: 10px 0;
      }
    }
  }

  .login-recaptcha {
    margin: 20px 0;
    ::v-deep div {
      margin: auto !important;
    }
  }

  .login-button {
    background-color: $primary;
    color: $secondary;
    border: none;
    margin: 20px 0;
    text-align: center !important;
    text-align: -moz-center !important;
    text-align: -webkit-center !important;
    font-family: $medium;

    &:hover,
    &:focus, &:active {
      background: $rgba-primary-1 !important;
    }
  }

  .forgotten-info-wrapper {
    .forgotten-info {
      margin-bottom: 2px;
    }
  }
}
</style>